'use client';
import { useState, useEffect } from 'react';
import { Users, Clock, Briefcase, FileCheck, Fingerprint, TrendingUp, CheckCircle2, FileText } from 'lucide-react';
import styles from './RhModule.module.css';

export function RhModule({ reverse = false }: { reverse?: boolean }) {
  const [activeMockup, setActiveMockup] = useState('ponto');
  const [time, setTime] = useState('');

  // Simula um relógio em tempo real no mockup de Ponto
  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      setTime(now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
    };
    updateClock();
    const interval = setInterval(updateClock, 1000);
    return () => clearInterval(interval);
  }, []);

  const getCardStyle = (isActive: boolean) => ({
    display: 'flex', gap: '1.25rem', alignItems: 'flex-start', padding: '1.25rem',
    borderRadius: '16px', cursor: 'pointer', transition: 'all 0.2s',
    border: isActive ? '1px solid var(--md-sys-color-primary)' : '1px solid #e2e8f0',
    background: isActive ? 'color-mix(in srgb, var(--md-sys-color-primary) 5%, transparent)' : 'white',
    boxShadow: isActive ? '0 4px 12px rgba(245,124,0,0.05)' : '0 2px 4px rgba(0,0,0,0.02)'
  });

  return (
    <div style={{ display: 'flex', flexDirection: reverse ? 'row-reverse' : 'row', gap: '4rem', alignItems: 'center', flexWrap: 'wrap' }}>
      
      {/* TEXTO E CARDS DE SELEÇÃO */}
      <div style={{ flex: '1 1 450px', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        <div>
          <span style={{background: '#e0e7ff', color: '#4f46e5', padding: '6px 16px', borderRadius: '50px', fontSize: '0.8rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px'}}>
            Recursos Humanos
          </span>
          <h3 style={{ fontSize: '2.5rem', fontWeight: 800, color: '#0f172a', margin: '1.5rem 0 1rem 0', lineHeight: 1.2 }}>
            Gestão de Equipe e Ponto Digital
          </h3>
          <p style={{ fontSize: '1.1rem', color: '#64748b', lineHeight: 1.6 }}>
            Administre funcionários, acompanhe o banco de horas ao vivo, organize documentos e gerencie processos seletivos de ponta a ponta.
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem', maxHeight: '550px', overflowY: 'auto', paddingRight: '8px' }}>
          
          <div style={getCardStyle(activeMockup === 'ponto')} onClick={() => setActiveMockup('ponto')}>
            <Clock size={28} style={{ color: '#4f46e5', flexShrink: 0, marginTop: '2px' }} />
            <div>
              <h4 style={{ margin: '0 0 4px 0', fontSize: '1.05rem', color: '#1e293b', fontWeight: 700 }}>Controle de Ponto e Horas</h4>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748b', lineHeight: 1.4 }}>Bate-ponto digital via web/app. Calcule automaticamente horas extras e banco de horas.</p>
            </div>
          </div>
          
          <div style={getCardStyle(activeMockup === 'equipe')} onClick={() => setActiveMockup('equipe')}>
            <Users size={28} style={{ color: '#10b981', flexShrink: 0, marginTop: '2px' }} />
            <div>
              <h4 style={{ margin: '0 0 4px 0', fontSize: '1.05rem', color: '#1e293b', fontWeight: 700 }}>Gestão de Colaboradores</h4>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748b', lineHeight: 1.4 }}>Cadastro completo, controle de férias, faltas e organograma da empresa em um só lugar.</p>
            </div>
          </div>

          <div style={getCardStyle(activeMockup === 'vagas')} onClick={() => setActiveMockup('vagas')}>
            <Briefcase size={28} style={{ color: '#f59e0b', flexShrink: 0, marginTop: '2px' }} />
            <div>
              <h4 style={{ margin: '0 0 4px 0', fontSize: '1.05rem', color: '#1e293b', fontWeight: 700 }}>Recrutamento e Vagas</h4>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748b', lineHeight: 1.4 }}>Integração com LinkedIn e portal de vagas. Receba currículos e trie candidatos por IA.</p>
            </div>
          </div>

          <div style={getCardStyle(activeMockup === 'documentos')} onClick={() => setActiveMockup('documentos')}>
            <FileCheck size={28} style={{ color: '#3b82f6', flexShrink: 0, marginTop: '2px' }} />
            <div>
              <h4 style={{ margin: '0 0 4px 0', fontSize: '1.05rem', color: '#1e293b', fontWeight: 700 }}>GED (Documentos do RH)</h4>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748b', lineHeight: 1.4 }}>Contratos, atestados e envio de holerites em PDF direto para o WhatsApp do funcionário.</p>
            </div>
          </div>

        </div>
      </div>

      {/* MOCKUP DESKTOP (DIREITA) */}
      <div style={{ flex: '1 1 450px', width: '100%', minWidth: 0 }}>
        <div className={styles.mockupContainer}>
          <div className={styles.desktopMockup}>
            
            <div className={styles.browserBar}>
              <div className={styles.dots}><span></span><span></span><span></span></div>
              <div className={styles.addressBar}>
                painel.aesirerp.com/rh/{activeMockup}
              </div>
            </div>

            <div className={styles.dashboardBody}>
              
              {/* 1. Controle de Ponto */}
              {activeMockup === 'ponto' && (
                <>
                  <div className={styles.clockCard}>
                    <div className={styles.currentDate}>Quarta-feira, 22 de Abril</div>
                    <div className={styles.currentTime}>{time || '08:00:00'}</div>
                    <button className={styles.punchBtn}>
                      <Fingerprint size={20} /> Bater Ponto
                    </button>

                    <div className={styles.punchList}>
                      <div className={styles.punchItem}>
                        <span className={styles.punchLabel}>Entrada 1</span>
                        <span className={styles.punchTime}>08:01</span>
                      </div>
                      <div className={styles.punchItem}>
                        <span className={styles.punchLabel}>Saída 1</span>
                        <span className={styles.punchTime}>12:05</span>
                      </div>
                      <div className={styles.punchItem}>
                        <span className={styles.punchLabel}>Entrada 2</span>
                        <span className={styles.punchTime}>13:00</span>
                      </div>
                      <div className={styles.punchItem}>
                        <span className={styles.punchLabel}>Saída 2</span>
                        <span className={`${styles.punchTime} ${styles.punchMissing}`}>--:--</span>
                      </div>
                    </div>
                  </div>

                  <div className={styles.balanceCard}>
                    <div className={styles.balanceInfo}>
                      <h4>Saldo do Banco de Horas</h4>
                      <p>+ 12h 45m</p>
                    </div>
                    <TrendingUp size={32} opacity={0.8} />
                  </div>
                </>
              )}

              {/* 2. Gestão de Equipe */}
              {activeMockup === 'equipe' && (
                <div className={styles.listContainer}>
                  <div className={styles.listHeader}>Colaboradores</div>
                  <div className={styles.listBody}>
                    <div className={styles.empRow}>
                      <div className={styles.empAvatar}>JS</div>
                      <div className={styles.empInfo}>
                        <span className={styles.empName}>João Silva</span>
                        <span className={styles.empRole}>Vendedor Sênior</span>
                      </div>
                      <span className={`${styles.empStatus} ${styles.statusActive}`}>ATIVO</span>
                    </div>
                    <div className={styles.empRow}>
                      <div className={styles.empAvatar}>MC</div>
                      <div className={styles.empInfo}>
                        <span className={styles.empName}>Maria Costa</span>
                        <span className={styles.empRole}>Suporte N2</span>
                      </div>
                      <span className={`${styles.empStatus} ${styles.statusVacation}`}>FÉRIAS</span>
                    </div>
                    <div className={styles.empRow}>
                      <div className={styles.empAvatar}>PA</div>
                      <div className={styles.empInfo}>
                        <span className={styles.empName}>Pedro Alves</span>
                        <span className={styles.empRole}>Desenvolvedor Backend</span>
                      </div>
                      <span className={`${styles.empStatus} ${styles.statusActive}`}>ATIVO</span>
                    </div>
                  </div>
                </div>
              )}

              {/* 3. Recrutamento e Vagas */}
              {activeMockup === 'vagas' && (
                <>
                  <div className={styles.jobCard}>
                    <div className={styles.jobHeader}>
                      <span className={styles.jobTitle}>Desenvolvedor Frontend</span>
                      <span className={styles.jobPlatform}>LinkedIn Integrado</span>
                    </div>
                    <div className={styles.candidateList}>
                      <div className={styles.candidateCard}>
                        <span className={styles.candName}>Lucas F.</span>
                        <span className={styles.candMatch}><CheckCircle2 size={12}/> 95% Fit</span>
                      </div>
                      <div className={styles.candidateCard}>
                        <span className={styles.candName}>Ana P.</span>
                        <span className={styles.candMatch}><CheckCircle2 size={12}/> 88% Fit</span>
                      </div>
                      <div className={styles.candidateCard}>
                        <span className={styles.candName}>Thiago R.</span>
                        <span className={styles.candMatch} style={{color: '#f59e0b'}}><Clock size={12}/> Triagem</span>
                      </div>
                    </div>
                  </div>

                  <div className={styles.jobCard}>
                    <div className={styles.jobHeader}>
                      <span className={styles.jobTitle}>Atendente Comercial</span>
                      <span className={styles.jobPlatform}>Portal de Vagas Aesir</span>
                    </div>
                    <div style={{fontSize: '0.8rem', color: '#64748b'}}>12 currículos aguardando análise.</div>
                  </div>
                </>
              )}

              {/* 4. Documentos (GED) */}
              {activeMockup === 'documentos' && (
                <div className={styles.listContainer}>
                  <div className={styles.listHeader}>Arquivos Recentes</div>
                  <div className={styles.listBody}>
                    <div className={styles.empRow}>
                      <FileText size={24} color="#ef4444" />
                      <div className={styles.empInfo}>
                        <span className={styles.empName}>Holerite_Maria_Marzo.pdf</span>
                        <span className={styles.empRole}>Enviado via WhatsApp há 2h</span>
                      </div>
                    </div>
                    <div className={styles.empRow}>
                      <FileCheck size={24} color="#3b82f6" />
                      <div className={styles.empInfo}>
                        <span className={styles.empName}>Contrato_Admissao_Joao.pdf</span>
                        <span className={styles.empRole}>Assinado digitalmente</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>

    </div>
  );
}